import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Send, Loader2, Check, X, RotateCcw } from "lucide-react";

function DiffView({ before, after }) {
  // Render only top-level keys present in `after` so admin sees what will change.
  const keys = Object.keys(after || {});
  if (keys.length === 0) return <div className="text-sm text-zinc-500">No changes proposed.</div>;
  return (
    <div className="space-y-4">
      {keys.map((k) => (
        <div key={k} className="border border-zinc-200">
          <div className="bg-zinc-50 px-3 py-2 label-caps text-xs flex justify-between">
            <span>{k}</span>
            <span className="text-[#FF2B00]">CHANGED</span>
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
            <div className="p-3">
              <div className="label-caps text-zinc-400 text-[10px] mb-2">Before</div>
              <pre className="text-xs whitespace-pre-wrap break-words font-mono text-zinc-600">
                {JSON.stringify(before?.[k] ?? null, null, 2)}
              </pre>
            </div>
            <div className="p-3 bg-[#FF2B00]/5">
              <div className="label-caps text-[#FF2B00] text-[10px] mb-2">After</div>
              <pre className="text-xs whitespace-pre-wrap break-words font-mono">
                {JSON.stringify(after[k], null, 2)}
              </pre>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Customize() {
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // {message, proposed_changes}
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading, pending]);

  const send = async (textOverride) => {
    const message = (textOverride ?? input).trim();
    if (!message || loading) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput("");
    setLoading(true);
    setPending(null);
    try {
      const { data } = await api.post("/customize/chat", { message, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.proposed_changes && Object.keys(data.proposed_changes).length > 0) {
        setPending({ message: data.reply, proposed_changes: data.proposed_changes });
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Customizer is unavailable — try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const approve = async () => {
    if (!pending) return;
    setLoading(true);
    try {
      const { data } = await api.post("/customize/apply", { changes: pending.proposed_changes });
      setSettings(data);
      setMessages((m) => [...m, { role: "assistant", text: "✓ Changes applied." }]);
      setPending(null);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Failed to apply changes." }]);
    } finally {
      setLoading(false);
    }
  };

  const reject = () => {
    setMessages((m) => [...m, { role: "assistant", text: "✗ Changes discarded." }]);
    setPending(null);
  };

  const SUGGESTIONS = [
    "Rebrand the app to 'Cascade Auto Group' with a forest green accent",
    "Add a new service type called Windshield Repair with key windshield",
    "Add an onboarding template for Bodyshop technicians with 6 steps",
    "Add a doc template for vehicle release authorization",
    "Tighten the Carwash AI prompt to focus on luxury detailing standards",
    "Set default service priority to 'high' and SLA target to 12 hours",
  ];

  if (!settings) return <div className="p-12 text-zinc-500 label-caps">Loading customizer…</div>;

  return (
    <div className="p-6 lg:p-12" data-testid="customize-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-[#FF2B00]">Workspace</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Customizer Agent</h1>
          <p className="text-zinc-600 mt-2 text-sm max-w-xl">
            Describe how you want EAG to look and behave. The agent proposes a diff — you approve before anything is saved.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Chat */}
        <div className="lg:col-span-7 bg-white border border-zinc-200 flex flex-col" style={{ minHeight: 600 }}>
          <div className="p-4 border-b border-zinc-200 bg-black text-white flex items-center gap-2">
            <Sparkles size={16} className="text-[#FF2B00]" />
            <div className="label-caps">Customizer ● admin only</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" style={{ maxHeight: 500 }}>
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-zinc-600">Try one of these:</p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`customize-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-left w-full px-3 py-2 border border-zinc-200 hover:border-black hover:bg-zinc-50 text-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                <div className={`inline-block max-w-[90%] px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-black text-white" : "bg-zinc-100 text-black border border-zinc-200"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-left">
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-100 text-sm">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}

            {pending && (
              <div className="border-2 border-[#FF2B00] p-4 bg-white" data-testid="customize-pending-diff">
                <div className="flex justify-between items-start mb-3">
                  <div className="label-caps text-[#FF2B00]">Proposed Changes</div>
                  <div className="flex gap-2">
                    <button data-testid="customize-reject" onClick={reject} className="border border-black px-3 py-1.5 label-caps text-xs hover:bg-black hover:text-white inline-flex items-center gap-1">
                      <X size={12} /> Reject
                    </button>
                    <button data-testid="customize-approve" onClick={approve} disabled={loading} className="bg-[#FF2B00] text-white px-3 py-1.5 label-caps text-xs hover:bg-black inline-flex items-center gap-1 disabled:opacity-50">
                      <Check size={12} /> Approve & Apply
                    </button>
                  </div>
                </div>
                <DiffView before={settings} after={pending.proposed_changes} />
              </div>
            )}
          </div>
          <div className="border-t border-zinc-200 p-4 flex items-center gap-2">
            <input
              data-testid="customize-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Describe a change…"
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
            />
            <button
              data-testid="customize-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="bg-[#FF2B00] text-white px-3 py-2 hover:bg-black disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>

        {/* Live settings preview */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white border border-zinc-200 p-5" data-testid="settings-branding">
            <div className="label-caps text-zinc-500 mb-3">Branding</div>
            <div className="space-y-1 text-sm">
              <div><span className="text-zinc-500">Name:</span> <span className="font-medium">{settings.branding?.app_name}</span></div>
              <div><span className="text-zinc-500">Tagline:</span> {settings.branding?.tagline}</div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">Accent:</span>
                <span className="inline-block w-4 h-4 border border-black" style={{ background: settings.branding?.accent_color }} />
                <span className="font-mono text-xs">{settings.branding?.accent_color}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-5" data-testid="settings-business">
            <div className="label-caps text-zinc-500 mb-3">Business</div>
            <div className="space-y-1 text-sm">
              <div><span className="text-zinc-500">Default priority:</span> <span className="capitalize">{settings.business?.default_priority}</span></div>
              <div><span className="text-zinc-500">Work hours:</span> {settings.business?.work_hours}</div>
              <div><span className="text-zinc-500">SLA target:</span> {settings.business?.sla_target_hours}h</div>
              {settings.business?.service_pricing && (
                <div className="mt-2">
                  <div className="label-caps text-[10px] text-zinc-400">Pricing</div>
                  {Object.entries(settings.business.service_pricing).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span className="capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono">${v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-5" data-testid="settings-custom-services">
            <div className="label-caps text-zinc-500 mb-3">Custom Service Types</div>
            {(settings.custom_service_types || []).length === 0 ? (
              <div className="text-xs text-zinc-400">None — ask the agent to add one.</div>
            ) : (
              <div className="space-y-1">
                {settings.custom_service_types.map((t) => (
                  <div key={t.key} className="text-sm flex justify-between">
                    <span>{t.label}</span>
                    <span className="font-mono text-xs text-zinc-400">{t.key}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-zinc-200 p-5" data-testid="settings-onboarding-templates">
            <div className="label-caps text-zinc-500 mb-3">Onboarding Templates ({(settings.onboarding_templates || []).length})</div>
            {(settings.onboarding_templates || []).map((t, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <div className="text-sm font-medium">{t.name}</div>
                <ul className="mt-1 space-y-0.5">
                  {t.steps?.map((s, j) => <li key={j} className="text-xs text-zinc-600">• {s}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-white border border-zinc-200 p-5" data-testid="settings-doc-templates">
            <div className="label-caps text-zinc-500 mb-3">Document Templates ({(settings.doc_templates || []).length})</div>
            {(settings.doc_templates || []).map((t, i) => (
              <div key={i} className="mb-2 last:mb-0">
                <div className="text-sm font-medium">{t.title}</div>
                <div className="text-xs text-zinc-600">{t.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
